import { CreditCard, LifeBuoy, LockKeyhole, Mail, MessageSquareMore, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useHousehold } from "@/hooks/useHousehold";
import { useLearnerSeatCutover } from "@/hooks/useLearnerSeatCutover";

interface LoginSecurityPanelProps {
  email?: string | null;
}

export default function LoginSecurityPanel({ email }: LoginSecurityPanelProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { household } = useHousehold();
  const { enabled: seatCutover } = useLearnerSeatCutover();
  const isOwner = !!household && household.owner_id === user?.id;

  return (
    <section className="glass-card p-6 space-y-5">
      <div className="flex items-start gap-4">
        <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl border border-primary/30 bg-primary/10 text-primary shadow-glow">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <div className="space-y-2">
          <div>
            <p className="text-sm font-medium uppercase tracking-[0.22em] text-muted-foreground">Login & Security</p>
            <h2 className="font-heading text-xl font-bold text-foreground">Keep your account protected and easy to recover.</h2>
          </div>
          <p className="text-sm leading-6 text-muted-foreground">
            Manage your email, update your password, and get guided recovery help without leaving HeirWise.
          </p>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-3xl border border-border/70 bg-background/50 p-4">
          <div className="mb-3 flex items-center gap-2 text-foreground">
            <Mail className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold">Current email</span>
          </div>
          <p className="text-sm text-muted-foreground break-all">{email || "No email available"}</p>
        </div>

        <div className="rounded-3xl border border-border/70 bg-background/50 p-4">
          <div className="mb-3 flex items-center gap-2 text-foreground">
            <LockKeyhole className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold">Credential controls</span>
          </div>
          <p className="text-sm text-muted-foreground">Change your email, update your password, and review recent security activity.</p>
        </div>

        <div className="rounded-3xl border border-border/70 bg-background/50 p-4">
          <div className="mb-3 flex items-center gap-2 text-foreground">
            <LifeBuoy className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold">Recovery help</span>
          </div>
          <p className="text-sm text-muted-foreground">Use guided reset flows or contact support if you get locked out.</p>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        {seatCutover && isOwner && (
          <Button variant="outline" onClick={() => navigate("/settings/billing")}>
            <CreditCard className="mr-2 h-4 w-4" aria-hidden="true" />
            Billing &amp; Subscription
          </Button>
        )}
        <Button onClick={() => navigate("/settings/security?panel=email")}>
          Change Email
        </Button>
        <Button variant="outline" onClick={() => navigate("/settings/security?panel=password") }>
          Change Password
        </Button>
        <Button variant="outline" onClick={() => navigate("/support?category=login_password_issue") }>
          <LifeBuoy className="mr-2 h-4 w-4" />
          Login Help
        </Button>
        <Button variant="ghost" onClick={() => navigate("/feedback?type=ux_ui_suggestion") }>
          <MessageSquareMore className="mr-2 h-4 w-4" />
          Suggest an improvement
        </Button>
      </div>
    </section>
  );
}
